import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { API_CONFIG } from '../../config/api';

const ApiTesting = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [endpoint, setEndpoint] = useState('text');
  const [language, setLanguage] = useState('en');
  const [detailLevel, setDetailLevel] = useState('high');
  const [wordLimit, setWordLimit] = useState('');
  const [voice, setVoice] = useState('Joanna');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'ja', name: 'Japanese' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'ru', name: 'Russian' },
    { code: 'ko', name: 'Korean' },
    { code: 'zh', name: 'Chinese' }
  ];

  const voices = [
    'Joanna', 'Matthew', 'Amy', 'Brian', 'Emma', 'Olivia', 'Aria',
    'Lucia', 'Mizuki', 'Celine', 'Marlene', 'Carla', 'Camila'
  ];

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }
      
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      
      setSelectedFile(file);
      setError(null);
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  // Add cleanup for API requests
const [controller, setController] = useState(null);

// Cleanup function for ongoing requests
useEffect(() => {
  return () => {
    // Cancel any ongoing API requests when component unmounts
    if (controller) {
      controller.abort();
    }
  };
}, [controller]);

const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!selectedFile) {
      setError('Please select an image file');
      return;
    }
    
    if (!apiKey.trim()) {
      setError('Please enter your API key');
      return;
    }

    // Validate word limit
    if (detailLevel === 'limited' && wordLimit) {
      const limit = parseInt(wordLimit);
      if (isNaN(limit) || limit < 1 || limit > 500) {
        setError('Word limit must be a number between 1 and 500');
        return;
      }
    }

    // Cancel any previous ongoing request
    if (controller) {
      controller.abort();
    }

    // Create a new AbortController for this request
    const newController = new AbortController();
    setController(newController);

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Convert file to base64
      const base64Image = await convertFileToBase64(selectedFile);
      
      // Prepare request body
      const requestBody = {
        image: base64Image,
        language: language
      };

      // Add detail level and word limit
      requestBody.detail_level = detailLevel;
      if (detailLevel === 'limited' && wordLimit) {
        requestBody.word_limit = parseInt(wordLimit);
      }

      // Add voice for audio endpoint
      if (endpoint === 'audio') {
        requestBody.voice = voice;
      }

      // Make API request with abort signal
      const response = await axios.post(
        `${API_CONFIG.IMAGE_API_URL}/describe/${endpoint}`,
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Api-Key': apiKey
          },
          signal: newController.signal
        }
      );

      // Only update state if the component is still mounted and this is the latest request
      setResult(response.data);
      
      // If audio response, prepare audio for playback
      if (response.data.audio && audioRef.current) {
        audioRef.current.src = `data:audio/mp3;base64,${response.data.audio}`;
      }

    } catch (err) {
      // Don't update state if request was aborted
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        console.log('Request was cancelled');
        return;
      }
      
      console.error('API Error:', err);
      
      if (err.response) {
        setError(err.response.data.error || `API Error: ${err.response.status}`);
      } else if (err.request) {
        setError('Network error: Unable to reach the API');
      } else {
        setError('Error: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const playAudio = () => {
    if (audioRef.current) {
      // Handle potential play() promise rejection (browsers may block autoplay)
      const playPromise = audioRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Audio playback started successfully
          })
          .catch(error => {
            console.error('Audio playback failed:', error);
            setError('Audio playback failed. Please try clicking the play button directly on the audio player.');
          });
      }
    }
  };

  const copyToClipboard = (text) => {
    try {
      navigator.clipboard.writeText(text);
      // Show temporary success message
      const tempSuccess = document.createElement('div');
      tempSuccess.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg';
      tempSuccess.textContent = 'Copied to clipboard!';
      document.body.appendChild(tempSuccess);
      
      setTimeout(() => {
        if (document.body.contains(tempSuccess)) {
          document.body.removeChild(tempSuccess);
        }
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
      setError('Failed to copy to clipboard. Please try selecting and copying manually.');
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">API Testing</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* API Key */}
          <div>
            <label htmlFor="apiKey" className="block text-sm font-medium text-gray-700 mb-2">
              API Key
            </label>
            <input
              type="password"
              id="apiKey"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter your API key"
              required
            />
          </div>

          {/* Endpoint Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Endpoint
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="text"
                  checked={endpoint === 'text'}
                  onChange={(e) => setEndpoint(e.target.value)}
                  className="mr-2"
                />
                Text Description
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="audio"
                  checked={endpoint === 'audio'}
                  onChange={(e) => setEndpoint(e.target.value)}
                  className="mr-2"
                />
                Audio Description
              </label>
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-2">
              Image File
            </label>
            <input
              type="file"
              id="image"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            {selectedFile && (
              <p className="mt-2 text-sm text-gray-600">
                Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </p>
            )}
          </div>

          {/* Language Selection */}
          <div>
            <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-2">
              Language
            </label>
            <select
              id="language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {languages.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.name} ({lang.code})
                </option>
              ))}
            </select>
          </div>

          {/* Detail Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Detail Level
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="high"
                  checked={detailLevel === 'high'}
                  onChange={(e) => setDetailLevel(e.target.value)}
                  className="mr-2"
                />
                High Detail
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="limited"
                  checked={detailLevel === 'limited'}
                  onChange={(e) => setDetailLevel(e.target.value)}
                  className="mr-2"
                />
                Limited Detail
              </label>
            </div>
          </div>

          {/* Word Limit (only for limited) */}
          {detailLevel === 'limited' && (
            <div>
              <label htmlFor="wordLimit" className="block text-sm font-medium text-gray-700 mb-2">
                Word Limit (Optional)
              </label>
              <input
                type="number"
                id="wordLimit"
                value={wordLimit}
                onChange={(e) => setWordLimit(e.target.value)}
                min="1"
                max="500"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 50 (leave empty for default)"
              />
              <p className="mt-1 text-sm text-gray-500">
                Limit the description to a specific number of words (1-500)
              </p>
            </div>
          )}

          {/* Voice Selection (only for audio) */}
          {endpoint === 'audio' && (
            <div>
              <label htmlFor="voice" className="block text-sm font-medium text-gray-700 mb-2">
                Voice
              </label>
              <select
                id="voice"
                value={voice}
                onChange={(e) => setVoice(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {voices.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2 px-4 rounded font-medium ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {loading ? 'Processing...' : `Test ${endpoint === 'text' ? 'Text' : 'Audio'} API`}
          </button>
        </form>

        {/* Error Display */}
        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Results Display */}
        {result && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded">
            <h3 className="text-lg font-medium text-green-800 mb-4">API Response</h3>
            
            {/* Description */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <div className="relative">
                <textarea
                  value={result.description}
                  readOnly
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50"
                />
                <button
                  onClick={() => copyToClipboard(result.description)}
                  className="absolute top-2 right-2 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Copy
                </button>
              </div>
            </div>

            {/* Audio Player (for audio endpoint) */}
            {result.audio && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Audio
                </label>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={playAudio}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Play Audio
                  </button>
                  <audio ref={audioRef} controls className="flex-1">
                    Your browser does not support the audio element.
                  </audio>
                </div>
              </div>
            )}

            {/* Response Metadata */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Format:</span> {result.format}
              </div>
              <div>
                <span className="font-medium">Language:</span> {result.language}
              </div>
              <div>
                <span className="font-medium">Detail Level:</span> {result.detail_level}
              </div>
              {result.word_limit && (
                <div>
                  <span className="font-medium">Word Limit:</span> {result.word_limit}
                </div>
              )}
              {result.voice && (
                <div>
                  <span className="font-medium">Voice:</span> {result.voice}
                </div>
              )}
            </div>

            {/* Raw JSON Response */}
            <details className="mt-4">
              <summary className="cursor-pointer text-sm font-medium text-gray-700">
                View Raw JSON Response
              </summary>
              <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-x-auto">
                {JSON.stringify(result, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiTesting;
